// src/hooks/useModal.ts
import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { useAppDispatch } from '@/store';
import { closeModal, openModal } from '@/store/slices/ui/UISlice';
import type { UiState } from '@/types/ui.types';
import { selectModal } from '@/store/selectors/ui';

type NonNullModal = NonNullable<UiState['modal']>;
type ModalType = NonNullModal extends { type: infer T } ? T : never;

export function useModal() {
  const dispatch = useAppDispatch();
  const modal = useSelector(selectModal);

  const open = useCallback((payload: UiState['modal']) => {
    dispatch(openModal(payload));
  }, [dispatch]);

  const close = useCallback(() => {
    dispatch(closeModal());
  }, [dispatch]);

  // Simple boolean check
  const isType = useCallback(
    (type: ModalType): boolean =>
      modal !== null && 'type' in modal && (modal).type === type,
    [modal]
  );

  // Typed getter: returns the narrowed modal or null
  const get = useCallback(
    (type: ModalType) =>
      (isType(type) ? (modal) : null),
    [isType, modal]
  );

  const isOpen = modal !== null;

  return { modal, open, close, isOpen, isType, get };
}
